import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Brain, Heart, Moon, Utensils, Wind, AlertCircle, Lock, Sparkles,
  Activity, CloudRain, Zap, Eye, Flame, Gauge, Search, Lightbulb
} from 'lucide-react';
import { useEmotionalCorrelations } from '@/hooks/useEmotionalCorrelations';
import { useSubscription, PRODUCTS } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  getCorrelationSleepVsBrainFog,
  getCorrelationEFTVsAnxietyAndPanic,
  getCorrelationMeditationVsRuminationAndIntrusions,
  getCorrelationUPFVsLowMoodAndFoodNoise,
  getCorrelationMovementVsSelfSabotageAndMentalFatigue
} from '@/utils/emotionalCorrelations';

// Metric definitions with grouping
const EMOTIONAL_SYMPTOMS = [
  { key: 'anxiety_score', label: 'Anxiety', icon: CloudRain, color: 'hsl(var(--chart-1))' },
  { key: 'low_mood_score', label: 'Low Mood', icon: Moon, color: 'hsl(var(--chart-2))' },
  { key: 'panic_intensity_score', label: 'Panic Intensity', icon: Zap, color: 'hsl(var(--chart-3))' },
  { key: 'trauma_activation_score', label: 'Trauma Activation', icon: Flame, color: 'hsl(var(--chart-4))' },
  { key: 'flashbacks_frequency_score', label: 'Flashbacks', icon: Eye, color: 'hsl(var(--chart-5))' },
  { key: 'irritability_score', label: 'Irritability', icon: Zap, color: 'hsl(var(--chart-1))' },
  { key: 'emotional_numbness_score', label: 'Emotional Numbness', icon: Heart, color: 'hsl(var(--chart-2))' },
];

const MENTAL_PATTERNS = [
  { key: 'rumination_score', label: 'Rumination', icon: Brain, color: 'hsl(var(--chart-1))' },
  { key: 'intrusive_thoughts_score', label: 'Intrusive Thoughts', icon: Lightbulb, color: 'hsl(var(--chart-2))' },
  { key: 'thought_looping_score', label: 'Thought Looping', icon: Activity, color: 'hsl(var(--chart-3))' },
  { key: 'self_sabotage_score', label: 'Self-Sabotage', icon: AlertCircle, color: 'hsl(var(--chart-4))' },
  { key: 'brain_fog_score', label: 'Brain Fog', icon: CloudRain, color: 'hsl(var(--chart-5))' },
  { key: 'concentration_difficulty_score', label: 'Concentration Difficulties', icon: Search, color: 'hsl(var(--chart-1))' },
  { key: 'memory_lapses_score', label: 'Memory Lapses', icon: Brain, color: 'hsl(var(--chart-2))' },
  { key: 'word_finding_difficulty_score', label: 'Word-Finding Difficulties', icon: Search, color: 'hsl(var(--chart-3))' },
  { key: 'mental_fatigue_score', label: 'Mental Fatigue', icon: Gauge, color: 'hsl(var(--chart-4))' },
  { key: 'food_noise_score', label: 'Food Noise', icon: Utensils, color: 'hsl(var(--chart-5))' },
  { key: 'compulsive_urges_score', label: 'Compulsive Urges', icon: Activity, color: 'hsl(var(--chart-1))' },
];

const DATE_RANGES = [
  { value: '14', label: 'Last 2 weeks' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

// Reflective blurbs for each metric
const METRIC_REFLECTIONS: Record<string, string> = {
  anxiety_score: "Anxiety often fluctuates with sleep, caffeine, and nervous system regulation practices. Notice what tends to precede calmer days.",
  low_mood_score: "Low mood can be influenced by food quality, movement, connection, and sleep. Your data may reveal gentle patterns over time.",
  panic_intensity_score: "Panic episodes often respond to EFT, breathwork, and grounding practices. Track what helps your system settle.",
  trauma_activation_score: "Trauma activation can be soothed by meditation, nature time, and nervous system tools. Notice what supports your regulation.",
  flashbacks_frequency_score: "Flashbacks may be influenced by sleep quality, stress levels, and grounding practices.",
  irritability_score: "Irritability often connects to sleep, blood sugar stability, and overall nervous system load.",
  emotional_numbness_score: "Emotional numbness can be a protective response. Movement and somatic practices may gently support reconnection.",
  rumination_score: "Rumination tends to decrease with meditation, movement, and good sleep. Notice what helps your mind settle.",
  intrusive_thoughts_score: "Intrusive thoughts often respond to meditation and mindfulness practices over time.",
  thought_looping_score: "Thought looping can be interrupted by movement, breathwork, or changing your environment.",
  self_sabotage_score: "Self-sabotage patterns often emerge when we're depleted. Notice what supports you in making aligned choices.",
  brain_fog_score: "Brain fog frequently connects to sleep quality, hydration, and food choices.",
  concentration_difficulty_score: "Concentration varies with sleep, stress, and nervous system state.",
  memory_lapses_score: "Memory tends to improve with good sleep, lower stress, and consistent routines.",
  word_finding_difficulty_score: "Word-finding difficulties often relate to fatigue and cognitive load.",
  mental_fatigue_score: "Mental fatigue responds to rest, movement balance, and nervous system regulation.",
  food_noise_score: "Food noise often decreases with stable blood sugar, adequate protein, and nervous system calm.",
  compulsive_urges_score: "Compulsive urges may lessen with nervous system regulation, EFT, and adequate rest.",
};

interface TimeSeriesData {
  date: string;
  value: number | null;
  displayDate: string;
}

interface ExtendedCorrelation {
  type: string;
  interpretation: string | null;
}

export function EmotionalPatternsExplorer() {
  const { correlations, isLoading: correlationsLoading, error } = useEmotionalCorrelations(60);
  const { productId, hasPremiumAccess } = useSubscription();
  const navigate = useNavigate();
  
  const [selectedCategory, setSelectedCategory] = useState<'emotional' | 'mental'>('emotional');
  const [selectedMetric, setSelectedMetric] = useState<string>('anxiety_score');
  const [dateRange, setDateRange] = useState<string>('30');
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [isLoadingTimeSeries, setIsLoadingTimeSeries] = useState(false);
  const [extendedCorrelations, setExtendedCorrelations] = useState<ExtendedCorrelation[]>([]);
  const [isLoadingCorrelations, setIsLoadingCorrelations] = useState(false);

  const isSanctuary = productId === PRODUCTS.MASTERY || hasPremiumAccess();

  // Fetch time series data for selected metric
  useEffect(() => {
    const fetchTimeSeries = async () => {
      setIsLoadingTimeSeries(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoadingTimeSeries(false);
        return;
      }

      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('emotional_symptom_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: true });

      if (!error && data) {
        const formattedData = data.map(row => {
          const metricValue = row[selectedMetric as keyof typeof row];
          return {
            date: row.date,
            value: typeof metricValue === 'number' ? metricValue : null,
            displayDate: format(new Date(row.date), 'MMM d')
          };
        });
        setTimeSeriesData(formattedData);
      } else {
        setTimeSeriesData([]);
      }
      
      setIsLoadingTimeSeries(false);
    };

    if (isSanctuary) {
      fetchTimeSeries();
    }
  }, [selectedMetric, dateRange, isSanctuary]);

  // Fetch extended correlations
  useEffect(() => {
    const fetchExtendedCorrelations = async () => {
      setIsLoadingCorrelations(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoadingCorrelations(false);
        return;
      }

      const days = parseInt(dateRange);
      const results: ExtendedCorrelation[] = [];

      try {
        const [sleepBrainFog, eftAnxiety, meditationRumination, upfMood, movementSabotage] = await Promise.all([
          getCorrelationSleepVsBrainFog(user.id, days).catch(() => null),
          getCorrelationEFTVsAnxietyAndPanic(user.id, days).catch(() => null),
          getCorrelationMeditationVsRuminationAndIntrusions(user.id, days).catch(() => null),
          getCorrelationUPFVsLowMoodAndFoodNoise(user.id, days).catch(() => null),
          getCorrelationMovementVsSelfSabotageAndMentalFatigue(user.id, days).catch(() => null),
        ]);

        if (sleepBrainFog?.interpretation) {
          results.push({ type: 'sleep_brain_fog', interpretation: sleepBrainFog.interpretation });
        }
        if (eftAnxiety?.interpretation) {
          results.push({ type: 'eft_anxiety_panic', interpretation: eftAnxiety.interpretation });
        }
        if (meditationRumination?.interpretation) {
          results.push({ type: 'meditation_rumination', interpretation: meditationRumination.interpretation });
        }
        if (upfMood?.interpretation) {
          results.push({ type: 'upf_mood_food_noise', interpretation: upfMood.interpretation });
        }
        if (movementSabotage?.interpretation) {
          results.push({ type: 'movement_sabotage_fatigue', interpretation: movementSabotage.interpretation });
        }
      } catch (err) {
        console.error('Error fetching extended correlations:', err);
      }

      setExtendedCorrelations(results);
      setIsLoadingCorrelations(false);
    };

    if (isSanctuary) {
      fetchExtendedCorrelations();
    }
  }, [dateRange, isSanctuary]);

  // Gate behind Sanctuary subscription
  if (!isSanctuary) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-xl">Emotional & Mental Patterns</CardTitle>
          <CardDescription>
            Sanctuary members get access to deep emotional correlations - connecting your mental symptoms with sleep, food, and nervous system practices.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Moon className="w-4 h-4" />
              <span>Sleep ↔ Anxiety & Brain Fog correlations</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Wind className="w-4 h-4" />
              <span>EFT/Tapping ↔ Panic & Anxiety tracking</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Brain className="w-4 h-4" />
              <span>Meditation ↔ Rumination & Intrusive Thoughts</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Utensils className="w-4 h-4" />
              <span>Food Quality ↔ Mood & Food Noise</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Activity className="w-4 h-4" />
              <span>Movement ↔ Self-Sabotage & Mental Fatigue</span>
            </div>
          </div>
          <Button onClick={() => navigate('/subscription')} className="w-full">
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Sanctuary
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentMetrics = selectedCategory === 'emotional' ? EMOTIONAL_SYMPTOMS : MENTAL_PATTERNS;
  const currentMetricInfo = currentMetrics.find(m => m.key === selectedMetric) || currentMetrics[0];
  const MetricIcon = currentMetricInfo.icon;

  const hasTimeSeriesData = timeSeriesData.some(d => d.value !== null);

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            Emotional & Mental Patterns
          </CardTitle>
          <CardDescription>
            Explore correlations between your emotional symptoms and lifestyle factors
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Category Tabs */}
          <Tabs value={selectedCategory} onValueChange={(v) => {
            setSelectedCategory(v as 'emotional' | 'mental');
            setSelectedMetric(v === 'emotional' ? 'anxiety_score' : 'rumination_score');
          }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="emotional" className="flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Emotional Symptoms
              </TabsTrigger>
              <TabsTrigger value="mental" className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Mental Patterns
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Metric & Date Range Selectors */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedMetric} onValueChange={setSelectedMetric}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select metric" />
              </SelectTrigger>
              <SelectContent>
                {currentMetrics.map((metric) => {
                  const Icon = metric.icon;
                  return (
                    <SelectItem key={metric.key} value={metric.key}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {metric.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Time Series Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <MetricIcon className="w-5 h-5" style={{ color: currentMetricInfo.color }} />
              </div>
              <div>
                <CardTitle className="text-base">{currentMetricInfo.label} Over Time</CardTitle>
                <CardDescription className="text-xs">
                  {DATE_RANGES.find(r => r.value === dateRange)?.label}
                </CardDescription>
              </div>
            </div>
            {hasTimeSeriesData && (
              <Badge variant="secondary" className="text-xs">
                {timeSeriesData.filter(d => d.value !== null).length} entries
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingTimeSeries ? (
            <Skeleton className="h-48 w-full" />
          ) : hasTimeSeriesData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    domain={[0, 10]} 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={currentMetricInfo.color}
                    strokeWidth={2}
                    dot={{ fill: currentMetricInfo.color, strokeWidth: 0, r: 3 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center">
              <div className="text-center p-6 rounded-lg bg-muted/30">
                <CloudRain className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  Not enough data yet for {currentMetricInfo.label.toLowerCase()}.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  As you log more, Therappi will reflect patterns back to you.
                </p>
              </div>
            </div>
          )}

          {/* Reflective blurb */}
          {hasTimeSeriesData && (
            <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-xs text-muted-foreground leading-relaxed">
                {METRIC_REFLECTIONS[selectedMetric] || "Your data will reveal patterns over time."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extended Correlations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            Pattern Insights
          </CardTitle>
          <CardDescription className="text-xs">
            Connections between your practices and emotional/mental symptoms
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingCorrelations ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : extendedCorrelations.length > 0 ? (
            <div className="space-y-3">
              {extendedCorrelations.map((correlation, index) => (
                <div key={index} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    {correlation.interpretation}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <p className="text-sm text-muted-foreground">
                No strong patterns have emerged just yet.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                As you continue logging practices alongside emotional symptoms, Therappi will gently reflect back the connections it notices.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Base Correlations from hook */}
      {correlationsLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : correlations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Additional Correlations</CardTitle>
            <CardDescription className="text-xs">
              From your emotional symptom logs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {correlations.slice(0, 5).map((correlation) => (
              <div key={correlation.correlation_type} className="p-3 rounded-lg bg-muted/30 border border-border/30">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">
                    {correlation.metric_a} ↔ {correlation.metric_b}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {correlation.sample_size} days
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {correlation.interpretation}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Disclaimer */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            These reflections are drawn from your personal data and are offered with curiosity, not certainty. 
            They are not clinical diagnoses or guarantees - simply patterns that may help you notice what tends to support your wellbeing. 
            You know yourself best.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default EmotionalPatternsExplorer;
